import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell, PieChart, Pie } from 'recharts';
import {
  Trophy, Medal, BarChart3, Building2, Globe, Package, Users as UsersIcon,
  TrendingUp, TrendingDown, Minus, Filter, ChevronDown, ChevronLeft,
  FileSpreadsheet, X, Tag, CalendarOff, Edit, Check as CheckIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import SAR from '../components/SAR';
import { useAuth } from '../context/AuthContext';
import { ProductDetailTab } from '../components/ProductDetailTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'يومي' },
  { value: 'weekly', label: 'أسبوعي' },
  { value: 'monthly', label: 'شهري' },
  { value: 'quarterly', label: 'ربع سنوي (الحالي)' },
  { value: 'q1', label: 'الربع الأول (يناير - مارس)' },
  { value: 'q2', label: 'الربع الثاني (أبريل - يونيو)' },
  { value: 'q3', label: 'الربع الثالث (يوليو - سبتمبر)' },
  { value: 'q4', label: 'الربع الرابع (أكتوبر - ديسمبر)' },
  { value: 'custom', label: 'تاريخ مخصص' },
];

const today = () => new Date().toISOString().split('T')[0];
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

// Compute Sun..Sat date range for a Sunday-based week (year, week) — Gregorian.
// Week 1 = the week containing Jan 1 (Sunday-based). Mirrors backend _sun_week_range.
const isoWeekRange = (year, week) => {
  if (!year || !week) return null;
  const jan1 = new Date(Date.UTC(year, 0, 1));
  const dow = jan1.getUTCDay(); // 0=Sun..6=Sat
  const week1Sun = new Date(jan1);
  week1Sun.setUTCDate(jan1.getUTCDate() - dow);
  const start = new Date(week1Sun);
  start.setUTCDate(week1Sun.getUTCDate() + (week - 1) * 7);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  const iso = (d) => d.toISOString().split('T')[0];
  return { start: iso(start), end: iso(end) };
};

// Current Sunday-based week number (Gregorian).
const currentIsoWeek = () => {
  const now = new Date();
  const y = now.getFullYear();
  const jan1 = new Date(Date.UTC(y, 0, 1));
  const dow = jan1.getUTCDay();
  const week1Sun = new Date(jan1);
  week1Sun.setUTCDate(jan1.getUTCDate() - dow);
  const today_ = new Date(Date.UTC(y, now.getMonth(), now.getDate()));
  const delta = Math.floor((today_ - week1Sun) / 86400000);
  const week = Math.floor(delta / 7) + 1;
  return { year: y, week };
};

// Color-graded background for ranking row based on percentage
const rankBg = (pct) => {
  if (pct >= 100) return 'bg-emerald-50 border-emerald-200';
  if (pct >= 75) return 'bg-lime-50 border-lime-200';
  if (pct >= 50) return 'bg-amber-50 border-amber-200';
  if (pct >= 25) return 'bg-orange-50 border-orange-200';
  return 'bg-red-50 border-red-200';
};
const rankPctColor = (pct) => {
  if (pct >= 100) return 'text-emerald-700';
  if (pct >= 75) return 'text-lime-700';
  if (pct >= 50) return 'text-amber-700';
  if (pct >= 25) return 'text-orange-700';
  return 'text-red-700';
};
const rankBarFill = (pct) => {
  if (pct >= 100) return '#10B981';
  if (pct >= 75) return '#84CC16';
  if (pct >= 50) return '#F59E0B';
  if (pct >= 25) return '#F97316';
  return '#EF4444';
};

const TrendBadge = ({ trend, value }) => {
  const cfg = {
    up: { Icon: TrendingUp, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'صعود' },
    down: { Icon: TrendingDown, cls: 'bg-red-50 text-red-700 border-red-200', label: 'هبوط' },
    flat: { Icon: Minus, cls: 'bg-slate-50 text-slate-600 border-slate-200', label: 'ثابت' },
    new: { Icon: TrendingUp, cls: 'bg-blue-50 text-blue-700 border-blue-200', label: 'جديد' },
    lost: { Icon: TrendingDown, cls: 'bg-rose-50 text-rose-700 border-rose-200', label: 'مفقود' },
  }[trend] || { Icon: Minus, cls: 'bg-slate-50 text-slate-500 border-slate-200', label: '—' };
  const { Icon, cls, label } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-bold ${cls}`}>
      <Icon className="h-3 w-3" />
      {label}{value !== undefined ? <span className="font-numbers ml-1">{value}%</span> : null}
    </span>
  );
};

export default function SuperAnalytics({ branchScoped = false }) {
  const { user } = useAuth();
  // Editable target only available when:
  // 1) Branch-scoped (mudir / supervisor) → editable for their own branch
  // 2) Super admin acting as a specific branch → editable for that branch
  const editableBranchId = branchScoped ? user?.branch_id : (user?.acting_branch_id || null);
  const canEditTarget = !!editableBranchId;
  const [editingTarget, setEditingTarget] = useState(false);
  const [editTargetValue, setEditTargetValue] = useState('');
  const [savingTarget, setSavingTarget] = useState(false);
  const [tab, setTab] = useState('ranking'); // ranking | employees | branches | growth
  const [period, setPeriod] = useState('weekly');
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  // Specific ISO week selector — only used when period === 'weekly'
  const _now = currentIsoWeek();
  const [weekYear, setWeekYear] = useState(_now.year);
  const [weekNumber, setWeekNumber] = useState(null); // null = current week (use backend default)
  const [growthDim, setGrowthDim] = useState('product'); // product | country
  const [loading, setLoading] = useState(false);

  const [ranking, setRanking] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [compareMode, setCompareMode] = useState('same');
  // Unfiltered datasets used to power the donut indicators (the donuts always reflect the full network)
  const [rankingUnfiltered, setRankingUnfiltered] = useState(null);
  const [breakdownUnfiltered, setBreakdownUnfiltered] = useState(null);
  const [empDetail, setEmpDetail] = useState(null); // when an employee is opened
  const [empOpenId, setEmpOpenId] = useState(null);
  const [exporting, setExporting] = useState(false);
  // Interactive donut filter — combinable. Keys: 'branch'|'country'|'product'.
  // Each value: { value: string, label: string }
  const [drillFilters, setDrillFilters] = useState({});
  // Discount-code filter: null = no filter, 'any' = any discount, '10'|'15'|'25'|'50'|'96' = specific coupon % (96 = ND96)
  const [discountPct, setDiscountPct] = useState(null);
  // Product-kind filter: null = both, 'primary' = أساسي, 'additional' = إضافي
  const [productKind, setProductKind] = useState(null);
  // Catalog of products (loaded once) used by the explicit "فلتر المنتج" dropdown
  const [productCatalog, setProductCatalog] = useState([]);
  // Dynamic discount coupons from /api/coupons so the chip row grows when
  // super-admin adds a new coupon (e.g. PD50).
  const [coupons, setCoupons] = useState([]);

  // API path differs for branch-scoped (admin) vs network-wide (super_admin)
  const analyticsBase = branchScoped ? '/api/analytics/branch' : '/api/super/analytics';
  const breakdownPath = branchScoped ? '/api/analytics/branch/breakdown' : '/api/super/analytics/branch-breakdown';
  const productCatalogUrl = branchScoped ? `${API_URL}/api/products` : `${API_URL}/api/super/products`;

  useEffect(() => {
    axios
      .get(productCatalogUrl)
      .then((r) => setProductCatalog(r.data || []))
      .catch(() => setProductCatalog([]));
    // Analytics filter chips must include disabled coupons too — a retired
    // coupon can still have historical sales we need to filter by.
    axios
      .get(`${API_URL}/api/coupons?include_inactive=1`)
      .then((r) => setCoupons(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCoupons([]));
  }, [productCatalogUrl]);

  // Chips displayed in the discount filter row. Dynamic from DB — includes
  // disabled coupons so historical sales under a retired coupon are still
  // filterable. Falls back to the legacy hardcoded set if the request fails.
  const couponChips = React.useMemo(() => {
    if (coupons && coupons.length) {
      return coupons.map((c) => ({
        val: String(c.pct),
        label: c.active ? c.label : `${c.label} (معطّل)`,
      }));
    }
    return [
      { val: '10', label: '10%' },
      { val: '15', label: '15%' },
      { val: '25', label: '25%' },
      { val: '50', label: '50%' },
      { val: '96', label: 'ND96' },
      { val: '51', label: 'PD50' },
    ];
  }, [coupons]);

  const filterParams = useCallback((p) => {
    if (drillFilters.branch) p.append('branch_id', drillFilters.branch.value);
    if (drillFilters.country) p.append('country', drillFilters.country.value);
    if (drillFilters.product) p.append('product_name', drillFilters.product.value);
    if (discountPct && !p.has('discount_pct')) p.append('discount_pct', discountPct);
    if (productKind && !p.has('product_kind')) p.append('product_kind', productKind);
    return p;
  }, [drillFilters, discountPct, productKind]);

  const setDimFilter = useCallback((type, payload) => {
    setDrillFilters((prev) => {
      const next = { ...prev };
      if (payload == null) delete next[type];
      else next[type] = payload;
      return next;
    });
  }, []);
  const clearAllDrillFilters = useCallback(() => setDrillFilters({}), []);
  const hasDrillFilters = Object.keys(drillFilters).length > 0;

  const buildParams = useCallback(() => {
    // When weekly + a specific week is picked → translate to custom date range, but keep target_period=weekly
    if (period === 'weekly' && weekNumber) {
      const r = isoWeekRange(weekYear, weekNumber);
      if (r) {
        const p = new URLSearchParams({ period: 'custom' });
        p.append('start_date', r.start);
        p.append('end_date', r.end);
        p.append('target_period', 'weekly');
        return p;
      }
    }
    const p = new URLSearchParams({ period });
    if (period === 'custom') { p.append('start_date', startDate); p.append('end_date', endDate); }
    return p;
  }, [period, startDate, endDate, weekYear, weekNumber]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const baseParams = buildParams();
      // product_kind and discount_pct are DATASET-level filters — unlike branch/country/product
      // drill filters, they must apply to BOTH filtered and "unfiltered" (donut) fetches so
      // every KPI/donut (including the branch donut) reflects the same underlying sales universe.
      if (productKind) baseParams.append('product_kind', productKind);
      if (discountPct) baseParams.append('discount_pct', discountPct);
      const baseStr = baseParams.toString();
      const filtered = filterParams(new URLSearchParams(baseStr)).toString();
      const [r1, r2, r3, r1u, r2u] = await Promise.all([
        axios.get(`${API_URL}${analyticsBase}/employees-ranking?${filtered}`),
        axios.get(`${API_URL}${breakdownPath}?${filtered}`),
        axios.get(`${API_URL}${analyticsBase}/cross-growth?dimension=${growthDim}&${filtered}`),
        // Unfiltered copies powering the donut indicators (network-wide, but still
        // scoped to the selected product_kind — see baseStr above).
        axios.get(`${API_URL}${analyticsBase}/employees-ranking?${baseStr}`),
        axios.get(`${API_URL}${breakdownPath}?${baseStr}`),
      ]);
      setRanking(r1.data);
      setBreakdown(r2.data);
      setGrowth(r3.data);
      setRankingUnfiltered(r1u.data);
      setBreakdownUnfiltered(r2u.data);
      if (drillFilters.product) {
        const r4 = await axios.get(`${API_URL}${analyticsBase}/product-detail?${filtered}&compare=${compareMode}`);
        setProductDetail(r4.data);
      } else {
        setProductDetail(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [buildParams, filterParams, growthDim, analyticsBase, breakdownPath, productKind, discountPct, drillFilters.product, compareMode]);

  useEffect(() => {
    if (!drillFilters.product && tab === 'product') setTab('ranking');
  }, [drillFilters.product, tab]);

  useEffect(() => {
    // Always refetch when filters change. For custom period, only fetch when both dates are set.
    if (period !== 'custom' || (startDate && endDate)) fetchAll();
  }, [period, growthDim, drillFilters, discountPct, productKind, fetchAll, startDate, endDate]);

  const openEmployee = async (id) => {
    setEmpOpenId(id);
    setEmpDetail(null);
    try {
      // Include the current drill-down filters (branch/country/product/discount) so
      // clicking «بنجلاديش» → employee shows THAT employee's sales for بنجلاديش only.
      const base = buildParams();
      if (productKind) base.append('product_kind', productKind);
      const p = filterParams(new URLSearchParams(base.toString())).toString();
      const res = await axios.get(`${API_URL}${analyticsBase}/employee/${id}/detail?${p}`);
      setEmpDetail(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل تحميل تفاصيل الموظف');
      setEmpOpenId(null);
    }
  };

  const [exportingPdf, setExportingPdf] = useState(false);
  const handleProductPdf = async () => {
    setExportingPdf(true);
    try {
      const p = filterParams(buildParams()).toString();
      const res = await axios.get(`${API_URL}${analyticsBase}/product-detail/export/pdf?${p}&compare=${compareMode}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `product_detail_${period}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('تم تصدير تقرير المنتج PDF');
    } catch (e) {
      toast.error('فشل تصدير PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Include the active drill-down filters (branch/country/product/discount/kind)
      // so the exported workbook matches exactly what the user has filtered on-screen.
      const p = filterParams(buildParams()).toString();
      const res = await axios.get(`${API_URL}${analyticsBase}/export/excel?${p}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${branchScoped ? 'branch' : 'super'}_analytics_${period}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('تم تصدير التقرير الشامل بنجاح');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل التصدير');
    } finally {
      setExporting(false);
    }
  };

  const TABS = [
    { key: 'ranking', label: 'ترتيب الموظفين', icon: Trophy },
    { key: 'employees', label: 'الموظفون تفصيلياً', icon: UsersIcon },
    ...(drillFilters.product ? [{ key: 'product', label: 'المنتج تفصيلياً', icon: Package }] : []),
    ...(branchScoped ? [] : [{ key: 'branches', label: 'الفروع × المنتجات × الدول', icon: Building2 }]),
    { key: 'growth', label: 'النمو والانخفاض', icon: TrendingUp },
  ];

  return (
    <div className="space-y-6" dir="rtl" data-testid="super-analytics-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-brand_blue/10 flex items-center justify-center">
            <BarChart3 className="h-6 w-6 text-brand_blue" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-black">
              <span className="font-brand">قِمَم</span> — {branchScoped ? 'التحليل الشامل' : 'تحليل شامل'}
            </h1>
            <p className="text-sm text-foreground/60 mt-1">
              {branchScoped
                ? 'رؤية موحّدة لأداء فرعك — الموظفين والمنتجات والدول'
                : 'رؤية موحّدة لأداء كل الفروع والموظفين والمنتجات والدول'}
            </p>
          </div>
        </div>
        <Button
          onClick={handleExport}
          disabled={exporting || loading}
          variant="outline"
          className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          data-testid="export-super-analytics-btn"
        >
          <FileSpreadsheet className="h-4 w-4" />
          {exporting ? 'جاري التصدير...' : 'تصدير شامل (Excel)'}
        </Button>
      </div>

      {/* Period filter */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-brand_blue" />
          <h3 className="font-bold text-sm">الفترة الزمنية</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="space-y-2">
            <Label>النوع</Label>
            <Select value={period} onValueChange={(v) => { setPeriod(v); if (v !== 'weekly') setWeekNumber(null); }}>
              <SelectTrigger data-testid="analytics-period-select" className="text-right">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {period === 'weekly' && (
            <>
              <div className="space-y-2">
                <Label>السنة</Label>
                <Select value={String(weekYear)} onValueChange={(v) => setWeekYear(Number(v))}>
                  <SelectTrigger className="text-right font-numbers" data-testid="analytics-week-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 6 }, (_, i) => _now.year - 3 + i).map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>رقم الأسبوع</Label>
                <Select value={weekNumber ? String(weekNumber) : 'current'} onValueChange={(v) => setWeekNumber(v === 'current' ? null : Number(v))}>
                  <SelectTrigger className="text-right font-numbers" data-testid="analytics-week-number">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">الأسبوع الحالي{weekYear === _now.year ? ` (${_now.week})` : ''}</SelectItem>
                    {Array.from({ length: 53 }, (_, i) => i + 1).map((w) => (
                      <SelectItem key={w} value={String(w)}>أسبوع {w}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {weekNumber && (() => {
                const r = isoWeekRange(weekYear, weekNumber);
                return r ? (
                  <div className="text-[11px] text-foreground/60 self-end pb-2 font-numbers">
                    {r.start} → {r.end}
                  </div>
                ) : null;
              })()}
            </>
          )}
          {period === 'custom' && (
            <>
              <div className="space-y-2">
                <Label>من</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="text-right font-numbers" />
              </div>
              <div className="space-y-2">
                <Label>إلى</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="text-right font-numbers" />
              </div>
              <Button onClick={fetchAll} disabled={loading} className="gap-2">
                <Filter className="h-4 w-4" />{loading ? 'جاري...' : 'تطبيق'}
              </Button>
            </>
          )}
        </div>
      </Card>

      {/* Product filter — pick a product to see its top-selling employees across all branches */}
      <Card className="p-4" data-testid="product-filter-card">
        <div className="flex items-center gap-2 mb-3">
          <Package className="h-4 w-4 text-brand_blue" />
          <h3 className="font-bold text-sm">فلتر المنتج — أفضل الموظفين مبيعاً لمنتج محدد</h3>
        </div>

        {/* Kind toggle (أساسي/إضافي) — narrows both the dropdown below AND the analytics data */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold text-foreground/70">التصنيف:</span>
          {[
            { value: null, label: 'الكل', cls: 'border-brand_blue bg-brand_blue/10 text-brand_blue' },
            { value: 'primary', label: 'أساسي', cls: 'border-emerald-600 bg-emerald-50 text-emerald-800' },
            { value: 'additional', label: 'إضافي', cls: 'border-purple-600 bg-purple-50 text-purple-800' },
          ].map((opt) => {
            const active = productKind === opt.value;
            return (
              <button
                key={String(opt.value)}
                type="button"
                data-testid={`product-kind-filter-${opt.value ?? 'all'}`}
                onClick={() => {
                  setProductKind(opt.value);
                  // If a specific product is currently selected but doesn't match the new kind → clear it
                  if (drillFilters.product && opt.value) {
                    const stillOk = productCatalog.some(
                      (pp) => pp.name === drillFilters.product.value && (pp.kind || 'primary') === opt.value,
                    );
                    if (!stillOk) setDimFilter('product', null);
                  }
                }}
                className={`px-3 py-1 rounded-md border text-xs font-bold transition-colors ${
                  active ? opt.cls : 'border-input bg-background text-foreground hover:bg-accent'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="space-y-2 md:col-span-2">
            <Label>اختر المنتج</Label>
            <Select
              value={drillFilters.product?.value || 'none'}
              onValueChange={(v) => {
                if (v === 'none') setDimFilter('product', null);
                else setDimFilter('product', { value: v, label: `منتج: ${v}` });
              }}
            >
              <SelectTrigger data-testid="product-filter-select" className="text-right">
                <SelectValue placeholder="جميع المنتجات (بدون فلتر)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— جميع المنتجات (بدون فلتر) —</SelectItem>
                {productCatalog
                  .filter((p) => !productKind || (p.kind || 'primary') === productKind)
                  .map((p) => (
                    <SelectItem key={p.master_id || p.id} value={p.name}>
                      <span className="inline-flex items-center gap-2">
                        {p.name}
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                            (p.kind || 'primary') === 'additional'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-emerald-100 text-emerald-800'
                          }`}
                        >
                          {(p.kind || 'primary') === 'additional' ? 'إضافي' : 'أساسي'}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-foreground/55">
              عند اختيار تصنيف («أساسي» أو «إضافي»)، تشمل كل المؤشرات في الأسفل مبيعات هذا التصنيف فقط عبر جميع الفروع.
            </p>
          </div>
          {drillFilters.product && (
            <Button
              variant="outline"
              onClick={() => setDimFilter('product', null)}
              className="gap-2"
              data-testid="product-filter-clear"
            >
              <X className="h-4 w-4" />
              إزالة الفلتر
            </Button>
          )}
        </div>
      </Card>

      {/* Active filters chip */}
      {(hasDrillFilters || discountPct || productKind) && (
        <div className="flex flex-wrap items-center gap-2" data-testid="active-filters-bar">
          {/* Quick "Export Excel" button so users can save the filtered view without
              scrolling back to the header. Mirrors the same behaviour used in
              Tableau/Sharing Reports. */}
          <button
            onClick={handleExport}
            disabled={exporting || loading}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs rounded-md bg-emerald-600 border border-emerald-600 text-white hover:bg-emerald-700 hover:border-emerald-700 transition font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="active-filters-export-btn"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" />
            {exporting ? 'جاري التصدير...' : 'تصدير Excel (مع الفلاتر)'}
          </button>
          {Object.entries(drillFilters).map(([type, f]) => (
            <div
              key={type}
              className="flex items-center gap-2 p-3 rounded-lg bg-brand_blue/5 border border-brand_blue/30 min-w-[220px]"
              data-testid={`drill-filter-chip-${type}`}
            >
              <Filter className="h-4 w-4 text-brand_blue" />
              <span className="text-sm font-bold text-brand_blue">
                {f.label}
              </span>
              <button
                onClick={() => setDimFilter(type, null)}
                className="mr-auto inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-white border border-brand_blue/30 text-brand_blue hover:bg-brand_blue hover:text-white transition"
                data-testid={`drill-filter-clear-${type}`}
              >
                <X className="h-3 w-3" />
                إزالة
              </button>
            </div>
          ))}
          {hasDrillFilters && Object.keys(drillFilters).length > 1 && (
            <button
              onClick={clearAllDrillFilters}
              className="inline-flex items-center gap-1 px-3 py-2 text-xs rounded-md bg-destructive/10 border border-destructive/30 text-destructive hover:bg-destructive hover:text-white transition font-bold"
              data-testid="drill-filter-clear-all"
            >
              <X className="h-3 w-3" />
              مسح الكل
            </button>
          )}
          {discountPct && (
            <div
              className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-300 flex-1 min-w-[260px]"
              data-testid="discount-filter-chip"
            >
              <Tag className="h-4 w-4 text-amber-700" />
              <span className="text-sm font-bold text-amber-800">
                مُصفّى: كود الخصم {discountPct === 'any' ? '(الكل)' : `(${discountPct}% فقط)`}
              </span>
              <button
                onClick={() => setDiscountPct(null)}
                className="mr-auto inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-white border border-amber-300 text-amber-700 hover:bg-amber-500 hover:text-white hover:border-amber-500 transition"
                data-testid="discount-filter-clear"
              >
                <X className="h-3 w-3" />
                إزالة
              </button>
            </div>
          )}
          {productKind && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg border flex-1 min-w-[240px] ${
                productKind === 'additional' ? 'bg-purple-50 border-purple-300' : 'bg-emerald-50 border-emerald-300'
              }`}
              data-testid="product-kind-filter-chip"
            >
              <Package className={`h-4 w-4 ${productKind === 'additional' ? 'text-purple-700' : 'text-emerald-700'}`} />
              <span className={`text-sm font-bold ${productKind === 'additional' ? 'text-purple-800' : 'text-emerald-800'}`}>
                مُصفّى: منتجات {productKind === 'additional' ? 'إضافية' : 'أساسية'}
              </span>
              <button
                onClick={() => setProductKind(null)}
                className={`mr-auto inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-white border transition ${
                  productKind === 'additional'
                    ? 'border-purple-300 text-purple-700 hover:bg-purple-600 hover:text-white hover:border-purple-600'
                    : 'border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:text-white hover:border-emerald-600'
                }`}
                data-testid="product-kind-filter-clear"
              >
                <X className="h-3 w-3" />
                إزالة
              </button>
            </div>
          )}
        </div>
      )}

      {/* KPI strip — overall totals + discount-code highlight */}
      {ranking?.totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-kpi-strip">
          <Card className="p-4">
            <p className="text-xs text-foreground/55">إجمالي المبيعات</p>
            <p className="text-2xl font-black font-numbers text-emerald-700 mt-1">
              {fmt(ranking.totals.achieved)} <SAR />
            </p>
            {ranking.totals.target >= 0 && (
              <div className="mt-2 pt-2 border-t border-border/60 space-y-1" data-testid="kpi-target-block">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground/55 flex items-center gap-1">
                    المستهدف
                    {canEditTarget && period === 'weekly' && weekNumber && !editingTarget && (
                      <button
                        type="button"
                        onClick={() => { setEditingTarget(true); setEditTargetValue(String(ranking.totals.target || '')); }}
                        className="p-0.5 rounded hover:bg-accent/60 text-primary"
                        title={`تعديل مستهدف أسبوع ${weekNumber} - ${weekYear}`}
                        data-testid="edit-week-target-btn"
                      >
                        <Edit className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                  {editingTarget ? (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={editTargetValue}
                        onChange={(e) => setEditTargetValue(e.target.value)}
                        className="h-7 text-xs font-numbers w-24 text-left"
                        autoFocus
                        data-testid="edit-week-target-input"
                      />
                      <button
                        type="button"
                        disabled={savingTarget}
                        onClick={async () => {
                          const wt = Number(editTargetValue);
                          if (!Number.isFinite(wt) || wt < 0) { toast.error('قيمة غير صحيحة'); return; }
                          if (!editableBranchId || !weekYear || !weekNumber) return;
                          setSavingTarget(true);
                          try {
                            await axios.put(`${API_URL}/api/branches/${editableBranchId}/weekly-targets/${weekYear}/${weekNumber}`, { weekly_target: wt });
                            toast.success(`تم حفظ مستهدف أسبوع ${weekNumber}`);
                            setEditingTarget(false);
                            // Force re-fetch
                            const reload = new Event('reload-analytics');
                            window.dispatchEvent(reload);
                            // Simple approach: re-run buildParams cycle via state change
                            setWeekNumber((v) => v); // no-op but triggers refetch via effects on same value (use setter)
                            // Trigger a fresh fetch by toggling weekYear/weekNumber re-set
                            setWeekYear(weekYear);
                            setWeekNumber(weekNumber);
                            // Real refresh: hit reload via window
                            setTimeout(() => { window.location.reload(); }, 600);
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || 'فشل الحفظ');
                          } finally { setSavingTarget(false); }
                        }}
                        className="p-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                        data-testid="save-week-target-btn"
                      >
                        <CheckIcon className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditingTarget(false); setEditTargetValue(''); }}
                        className="p-1 rounded bg-muted text-foreground hover:bg-accent"
                        data-testid="cancel-week-target-btn"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <span className="font-numbers font-bold text-foreground/80">
                      {fmt(ranking.totals.target)} <SAR />
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground/55">المتبقي</span>
                  <span className="font-numbers font-bold text-rose-600">
                    {fmt(ranking.totals.remaining)} <SAR />
                  </span>
                </div>
                {/* Progress bar */}
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${ranking.totals.percentage >= 100 ? 'bg-emerald-500' : ranking.totals.percentage >= 75 ? 'bg-lime-500' : ranking.totals.percentage >= 50 ? 'bg-amber-500' : ranking.totals.percentage >= 25 ? 'bg-orange-500' : 'bg-red-500'}`}
                    style={{ width: `${Math.min(100, ranking.totals.percentage)}%` }}
                  />
                </div>
                <div className="flex items-center justify-end text-[11px]">
                  <span className={`font-numbers font-bold ${rankPctColor(ranking.totals.percentage)}`}>
                    {ranking.totals.percentage}%
                  </span>
                </div>
              </div>
            )}
          </Card>
          <Card className="p-4">
            <p className="text-xs text-foreground/55">عدد المعاملات</p>
            <p className="text-2xl font-black font-numbers text-foreground mt-1">
              {ranking.totals.transactions}
            </p>
          </Card>
          <Card className="p-4 border-amber-200 bg-amber-50/40" data-testid="discount-amount-kpi">
            <p className="text-xs text-amber-700 flex items-center gap-1.5">
              <Tag className="h-3 w-3" />
              مبيعات كود الخصم
              {discountPct && (
                <span className="ms-auto inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-amber-500 text-white">
                  ● فلتر: {discountPct === 'any' ? 'الكل' : `${discountPct}%`}
                </span>
              )}
            </p>
            <p className="text-2xl font-black font-numbers text-amber-700 mt-1">
              {fmt(ranking.totals.discount_achieved)} <SAR />
            </p>
            <p className="text-[11px] text-amber-700/70 mt-0.5 font-numbers">
              {ranking.totals.discount_transactions} عملية
              {ranking.totals.discount_percentage > 0 && (
                <span className="ms-1">— {ranking.totals.discount_percentage}% من الإجمالي</span>
              )}
            </p>
            {/* Discount filter chip group — dynamically rendered from
                /api/coupons so super-admin can add or retire coupons without
                a code change. "الكل" / "أي خصم" come first; coupon chips
                follow in DB order. */}
            <div className="mt-2 flex flex-wrap gap-1" data-testid="discount-filter-group">
              {[{ val: 'any', label: 'الكل' }, ...couponChips].map(({ val, label }) => {
                const active = discountPct === val;
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setDiscountPct((cur) => (cur === val ? null : val))}
                    className={`text-[11px] font-bold py-1.5 px-2.5 rounded-md border transition ${
                      active
                        ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                        : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-100'
                    }`}
                    data-testid={`discount-filter-btn-${val}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-foreground/55">عدد الموظفين</p>
            <p className="text-2xl font-black font-numbers text-foreground mt-1">
              {ranking.total_employees}
            </p>
          </Card>
        </div>
      )}

      {/* Donut indicators — Branch donut shows full network; other donuts respect active filters */}
      <DonutSection
        ranking={ranking}
        breakdown={breakdown}
        rankingFull={rankingUnfiltered || ranking}
        breakdownFull={breakdownUnfiltered || breakdown}
        drillFilters={drillFilters}
        showBranchDonut={!branchScoped}
        onSliceClick={(type, value) => {
          // Resolve labels and convert names → ids when needed (for branch slices)
          const data = breakdownUnfiltered || breakdown;
          if (type === 'branch') {
            const b = (data?.branches || []).find((x) => x.branch_name === value);
            if (!b) return;
            // Toggle: clicking the same active branch clears it
            if (drillFilters.branch?.value === b.branch_id) setDimFilter('branch', null);
            else setDimFilter('branch', { value: b.branch_id, label: `فرع ${value}` });
          } else if (type === 'country') {
            if (drillFilters.country?.value === value) setDimFilter('country', null);
            else setDimFilter('country', { value, label: `دولة ${value}` });
          } else if (type === 'product') {
            if (drillFilters.product?.value === value) setDimFilter('product', null);
            else setDimFilter('product', { value, label: `منتج ${value}` });
          } else if (type === 'employee') {
            const r = ((rankingUnfiltered || ranking)?.rows || []).find((x) => x.name === value);
            if (!r) return;
            // Employee slice takes user straight to the employee detail view
            setTab('employees');
            setTimeout(() => openEmployee(r.id), 50);
          }
        }}
      />

      {/* Tab nav */}
      <div className="flex gap-1 border-b border-border overflow-x-auto" data-testid="analytics-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setEmpOpenId(null); }}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px whitespace-nowrap transition ${
                active ? 'border-brand_blue text-brand_blue' : 'border-transparent text-foreground/60 hover:text-foreground'
              }`}
              data-testid={`analytics-tab-${t.key}`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === 'ranking' && <RankingTab ranking={ranking} />}
      {tab === 'employees' && (
        <EmployeesTab
          ranking={ranking}
          empDetail={empDetail}
          empOpenId={empOpenId}
          onOpen={openEmployee}
          onClose={() => { setEmpOpenId(null); setEmpDetail(null); }}
          period={period}
        />
      )}
      {tab === 'product' && drillFilters.product && <ProductDetailTab data={productDetail} onExportPdf={handleProductPdf} exportingPdf={exportingPdf} compare={compareMode} onCompareChange={setCompareMode} />}
      {tab === 'branches' && <BranchesTab breakdown={breakdown} />}
      {tab === 'growth' && (
        <GrowthTab growth={growth} dim={growthDim} setDim={setGrowthDim} />
      )}
    </div>
  );
}

/* ─────────── Tab 1: Ranking ─────────── */
function RankingTab({ ranking }) {
  const [hideOnLeave, setHideOnLeave] = useState(false);
  if (!ranking) return <Card className="p-12 text-center text-foreground/50">جاري التحميل...</Card>;
  const allRows = ranking.rows || [];
  const onLeaveCount = allRows.filter((r) => r.attendance === 'on_leave').length;
  const rows = hideOnLeave ? allRows.filter((r) => r.attendance !== 'on_leave') : allRows;
  if (allRows.length === 0) return <Card className="p-8 text-center text-foreground/50">لا يوجد موظفين في هذه الفترة.</Card>;

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-amber-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-slate-400" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-700" />;
    return <span className="text-sm font-numbers font-bold text-foreground/50 w-5 text-center">{rank}</span>;
  };

  return (
    <Card className="p-0 overflow-hidden" data-testid="ranking-table-card">
      <div className="p-4 border-b border-border bg-accent/40 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <h3 className="font-bold flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> ترتيب أفضل الموظفين مبيعاً (حسب نسبة تحقيق الهدف)</h3>
          <p className="text-xs text-foreground/60 mt-1">
            مجموع {ranking.total_employees} موظف — تدريجياً من الأفضل إلى الأقل
            {onLeaveCount > 0 && (
              <span className="ms-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-bold text-[10px]">
                <CalendarOff className="h-2.5 w-2.5" />
                {onLeaveCount} في إجازة
              </span>
            )}
          </p>
        </div>
        {onLeaveCount > 0 && (
          <button
            type="button"
            onClick={() => setHideOnLeave((v) => !v)}
            className={`text-xs font-bold px-3 py-1.5 rounded-md border transition-colors flex items-center gap-1 ${
              hideOnLeave
                ? 'bg-orange-500 text-white border-orange-500'
                : 'bg-white text-orange-700 border-orange-300 hover:bg-orange-50'
            }`}
            data-testid="toggle-hide-on-leave"
          >
            <CalendarOff className="h-3 w-3" />
            {hideOnLeave ? `إظهار الموظفين في إجازة (${onLeaveCount})` : `إخفاء الموظفين في إجازة (${onLeaveCount})`}
          </button>
        )}
      </div>
      <div className="divide-y divide-border" data-testid="ranking-list">
        {rows.map((r) => (
          <div
            key={r.id}
            className={`flex flex-wrap items-center gap-3 sm:gap-4 p-4 border-r-4 ${rankBg(r.percentage)}`}
            data-testid={`ranking-row-${r.rank}`}
          >
            <div className="flex items-center gap-3 min-w-[180px]">
              <div className="h-9 w-9 rounded-full bg-white border border-border flex items-center justify-center">
                {getRankIcon(r.rank)}
              </div>
              <div>
                <p className="font-bold text-sm flex items-center gap-1.5 flex-wrap">
                  <span>{r.name}</span>
                  {r.attendance === 'on_leave' && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-bold text-[10px]" data-testid={`ranking-leave-${r.id}`}>
                      <CalendarOff className="h-2.5 w-2.5" />
                      إجازة
                    </span>
                  )}
                </p>
                <p className="text-xs text-foreground/55">
                  {r.branch_name || '—'}
                  {r.attendance === 'on_leave' && r.leave_period && (
                    <span className="font-numbers ms-1" dir="ltr">
                      • {r.leave_period.start} → {r.leave_period.end}
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 flex-1 text-xs">
              <div><span className="text-foreground/50">المستهدف:</span> <span className="font-numbers font-bold">{fmt(r.target)}</span></div>
              <div><span className="text-foreground/50">المحقق:</span> <span className="font-numbers font-bold text-emerald-700">{fmt(r.achieved)}</span></div>
              <div><span className="text-foreground/50">المعاملات:</span> <span className="font-numbers font-bold">{r.transactions}</span></div>
              <div><span className="text-foreground/50">المتبقي:</span> <span className="font-numbers font-bold">{fmt(r.remaining)}</span></div>
            </div>
            <div className="w-full sm:w-56 flex items-center gap-2">
              <div className="flex-1 h-2.5 bg-white rounded-full overflow-hidden border border-border">
                <div
                  className="h-full transition-all duration-500"
                  style={{ width: `${Math.min(r.percentage, 100)}%`, background: rankBarFill(r.percentage) }}
                />
              </div>
              <span className={`text-sm font-numbers font-black ${rankPctColor(r.percentage)}`} style={{ minWidth: 56, textAlign: 'left' }}>
                {r.percentage}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ─────────── Tab 2: Employees Detail ─────────── */
function EmployeesTab({ ranking, empDetail, empOpenId, onOpen, onClose }) {
  const [discountOnly, setDiscountOnly] = useState(false);
  if (!ranking) return <Card className="p-12 text-center text-foreground/50">جاري التحميل...</Card>;

  if (empOpenId && !empDetail) return <Card className="p-12 text-center text-foreground/50">جاري تحميل التفاصيل...</Card>;

  if (empDetail) {
    const emp = empDetail.employee;
    const allTxs = empDetail.transactions || [];
    const txs = discountOnly ? allTxs.filter((s) => s.used_discount_code) : allTxs;
    const discountCount = empDetail.discount_transactions || 0;
    return (
      <div className="space-y-4" data-testid="employee-detail-view">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onClose} className="gap-2" data-testid="employee-detail-back">
            <ChevronLeft className="h-4 w-4 rotate-180" />
            رجوع للقائمة
          </Button>
          <h3 className="font-bold flex-1 flex items-center gap-2 flex-wrap">
            <span>تفاصيل {emp.name} — {emp.branch_name || '—'}</span>
            {emp.attendance === 'on_leave' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-100 text-orange-700 font-bold text-xs" data-testid="detail-leave-badge">
                <CalendarOff className="h-3 w-3" />
                إجازة
                {emp.leave_period && (
                  <span className="font-numbers ms-1" dir="ltr">
                    ({emp.leave_period.start} → {emp.leave_period.end})
                  </span>
                )}
              </span>
            )}
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3"><p className="text-xs text-foreground/55">إجمالي المبيعات</p><p className="text-xl font-black font-numbers text-emerald-700">{fmt(empDetail.total)} <SAR /></p></Card>
          <Card className="p-3"><p className="text-xs text-foreground/55">عدد المعاملات</p><p className="text-xl font-black font-numbers">{empDetail.transactions_count}</p></Card>
          <Card className="p-3 cursor-pointer hover:ring-2 hover:ring-amber-300" onClick={() => setDiscountOnly((v) => !v)} data-testid="discount-stat-card">
            <p className="text-xs text-foreground/55 flex items-center gap-1"><Tag className="h-3 w-3 text-amber-600" />عمليات بكود خصم</p>
            <p className="text-xl font-black font-numbers text-amber-700">{discountCount}</p>
          </Card>
          <Card className="p-3"><p className="text-xs text-foreground/55">عدد الدول</p><p className="text-xl font-black font-numbers">{empDetail.countries.length}</p></Card>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Products */}
          <Card className="p-4">
            <h4 className="font-bold mb-3 flex items-center gap-2"><Package className="h-4 w-4 text-brand_blue" /> أداء المنتجات</h4>
            {empDetail.products.length === 0 ? <p className="text-xs text-foreground/40 py-8 text-center">لا توجد مبيعات</p> : (
              <table className="w-full text-xs">
                <thead><tr className="text-foreground/55 border-b border-border"><th className="py-2 text-right">المنتج</th><th className="py-2">الكمية</th><th className="py-2">المعاملات</th><th className="py-2 text-left">الإجمالي</th></tr></thead>
                <tbody className="divide-y divide-border">
                  {empDetail.products.map((p, i) => (
                    <React.Fragment key={i}>
                      <tr><td className="py-2 font-bold">{p.name}</td><td className="py-2 text-center font-numbers">{p.quantity}</td><td className="py-2 text-center font-numbers">{p.count}</td><td className="py-2 text-left font-numbers font-bold text-emerald-700">{fmt(p.total)}</td></tr>
                      {(p.options || []).map((o) => (
                        <tr key={`${i}-${o.label}`} className="bg-primary/5 !border-t-0" data-testid={`emp-product-option-${o.label}`}>
                          <td className="py-1.5 pr-4 text-[11px] text-primary">↳ {o.label}</td>
                          <td className="py-1.5 text-center font-numbers text-[11px]">{o.quantity}</td>
                          <td className="py-1.5 text-center font-numbers text-[11px]">{o.transactions}</td>
                          <td className="py-1.5 text-left font-numbers text-[11px] text-emerald-700">{fmt(o.total)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
          {/* Countries */}
          <Card className="p-4">
            <h4 className="font-bold mb-3 flex items-center gap-2"><Globe className="h-4 w-4 text-amber-600" /> أداء الدول</h4>
            {empDetail.countries.length === 0 ? <p className="text-xs text-foreground/40 py-8 text-center">لا توجد مبيعات</p> : (
              <table className="w-full text-xs">
                <thead><tr className="text-foreground/55 border-b border-border"><th className="py-2 text-right">الدولة</th><th className="py-2">المعاملات</th><th className="py-2 text-left">الإجمالي</th></tr></thead>
                <tbody className="divide-y divide-border">
                  {empDetail.countries.map((c, i) => (
                    <tr key={i}><td className="py-2 font-bold">{c.country}</td><td className="py-2 text-center font-numbers">{c.count}</td><td className="py-2 text-left font-numbers font-bold text-amber-700">{fmt(c.total)}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
        {/* Transactions */}
        <Card className="p-0 overflow-hidden">
          <div className="p-3 border-b border-border bg-accent/40 flex items-center gap-2">
            <h4 className="font-bold text-sm">عمليات البيع التفصيلية ({txs.length})</h4>
            <div className="flex-1" />
            <button
              onClick={() => setDiscountOnly((v) => !v)}
              className={`text-xs font-bold px-3 py-1 rounded-md border transition-colors flex items-center gap-1 ${
                discountOnly ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
              }`}
              data-testid="toggle-discount-filter"
            >
              <Tag className="h-3 w-3" />
              {discountOnly ? `كود خصم فقط (${txs.length})` : `عرض كود خصم فقط (${discountCount})`}
            </button>
          </div>
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="employee-tx-table">
              <thead className="bg-accent/30 sticky top-0">
                <tr className="text-foreground/55">
                  <th className="py-2 px-3 text-right">التاريخ</th>
                  <th className="py-2 px-3 text-right">المنتج</th>
                  <th className="py-2 px-3 text-right">الكمية</th>
                  <th className="py-2 px-3 text-right">الدولة</th>
                  <th className="py-2 px-3 text-right">العميل</th>
                  <th className="py-2 px-3 text-right">كود خصم</th>
                  <th className="py-2 px-3 text-left">المبلغ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {txs.length === 0 ? <tr><td colSpan="7" className="py-8 text-center text-foreground/40">لا توجد معاملات</td></tr> :
                  txs.map((s) => (
                    <tr key={s.id} className={`hover:bg-accent/40 ${s.used_discount_code ? 'bg-amber-50/40' : ''}`}>
                      <td className="py-2 px-3 font-numbers whitespace-nowrap" data-testid={`employee-tx-datetime-${s.id}`}>
                        {(s.date || '').slice(0, 10)}
                        {s.created_at && (
                          <span className="block text-[10px] text-foreground/55">
                            {new Date(s.created_at).toLocaleTimeString('en-US', { timeZone: 'Asia/Riyadh', hour: '2-digit', minute: '2-digit', hour12: true })}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        {s.product_name}
                        {s.price_option_label && <span className="block text-[10px] text-primary">{s.price_option_label}</span>}
                      </td>
                      <td className="py-2 px-3 font-numbers text-center">{s.quantity || 1}</td>
                      <td className="py-2 px-3">{s.country || '—'}</td>
                      <td className="py-2 px-3">{s.customer_name || '—'}</td>
                      <td className="py-2 px-3">
                        {s.used_discount_code ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 font-bold text-[10px]">
                            <Tag className="h-3 w-3" />
                            {s.discount_pct ? `كود خصم ${s.discount_pct}%` : 'كود خصم'}
                          </span>
                        ) : (
                          <span className="text-foreground/30">—</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-left font-numbers font-bold text-emerald-700">{fmt(s.amount)}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    );
  }

  // List of employees to drill into
  const rows = ranking.rows || [];
  return (
    <Card className="p-0 overflow-hidden" data-testid="employees-drill-list">
      <div className="p-4 border-b border-border bg-accent/40">
        <h3 className="font-bold">اضغط على الموظف لعرض عمليات البيع التفصيلية</h3>
      </div>
      <div className="divide-y divide-border">
        {rows.map((r) => (
          <button
            key={r.id}
            onClick={() => onOpen(r.id)}
            className="w-full flex items-center gap-3 p-4 hover:bg-accent/30 transition text-right"
            data-testid={`employee-drill-${r.id}`}
          >
            <div className="h-9 w-9 rounded-full bg-brand_blue/10 text-brand_blue flex items-center justify-center font-bold font-numbers text-sm">{r.rank}</div>
            <div className="flex-1">
              <p className="font-bold text-sm flex items-center gap-1.5 flex-wrap">
                <span>{r.name}</span>
                {r.attendance === 'on_leave' && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-bold text-[10px]" data-testid={`drill-leave-${r.id}`}>
                    <CalendarOff className="h-2.5 w-2.5" />
                    إجازة
                  </span>
                )}
              </p>
              <p className="text-xs text-foreground/55 flex items-center gap-1 flex-wrap">
                <span>{r.branch_name || '—'}</span>
                <span>•</span>
                <span>{r.transactions} معاملة</span>
                {r.discount_transactions > 0 && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold text-[10px]">
                    <Tag className="h-2.5 w-2.5" />
                    {r.discount_transactions} كود خصم
                  </span>
                )}
                <span>•</span>
                <span className="font-numbers">{fmt(r.achieved)} ر.س</span>
                {r.attendance === 'on_leave' && r.leave_period && (
                  <>
                    <span>•</span>
                    <span className="font-numbers" dir="ltr">{r.leave_period.start} → {r.leave_period.end}</span>
                  </>
                )}
              </p>
            </div>
            <span className={`px-2 py-0.5 rounded-md border text-xs font-bold font-numbers ${rankBg(r.percentage)} ${rankPctColor(r.percentage)}`}>
              {r.percentage}%
            </span>
            <ChevronDown className="h-4 w-4 text-foreground/40 -rotate-90" />
          </button>
        ))}
      </div>
    </Card>
  );
}

/* ─────────── Tab 3: Branches × Products × Countries ─────────── */
function BranchesTab({ breakdown }) {
  const [expanded, setExpanded] = useState({});
  if (!breakdown) return <Card className="p-12 text-center text-foreground/50">جاري التحميل...</Card>;
  const branches = breakdown.branches || [];
  if (branches.length === 0) return <Card className="p-8 text-center text-foreground/50">لا توجد فروع.</Card>;

  // Comparison chart: total per branch
  const chartData = branches.map(b => ({ name: b.branch_name, total: b.total }));

  return (
    <div className="space-y-4" data-testid="branches-breakdown">
      <Card className="p-4">
        <h3 className="font-bold mb-3 flex items-center gap-2"><Building2 className="h-4 w-4 text-brand_blue" /> مقارنة إجماليات الفروع</h3>
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#374151' }} />
              <YAxis tick={{ fontSize: 11, fill: '#374151' }} />
              <Tooltip formatter={(v) => `${fmt(v)} ر.س`} contentStyle={{ direction: 'rtl' }} />
              <Bar dataKey="total" name="الإجمالي" fill="#1E40AF" radius={[6, 6, 0, 0]}>
                {chartData.map((_, i) => <Cell key={i} fill={['#1E40AF', '#0F766E', '#B45309', '#9333EA'][i % 4]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      {branches.map((b) => {
        const open = !!expanded[b.branch_id];
        return (
          <Card key={b.branch_id} className="p-0 overflow-hidden" data-testid={`branch-card-${b.branch_slug || b.branch_id}`}>
            <button
              onClick={() => setExpanded(prev => ({ ...prev, [b.branch_id]: !open }))}
              className="w-full flex items-center gap-3 p-4 hover:bg-accent/30 transition text-right"
            >
              <Building2 className="h-5 w-5 text-brand_blue" />
              <div className="flex-1">
                <p className="font-bold">{b.branch_name}</p>
                <p className="text-xs text-foreground/55 font-numbers">{b.transactions} معاملة • {b.products.length} منتج • {b.countries.length} دولة</p>
              </div>
              <span className="text-emerald-700 font-numbers font-black">{fmt(b.total)} <SAR /></span>
              <ChevronDown className={`h-5 w-5 text-foreground/40 transition ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
              <div className="p-4 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-4 bg-accent/10">
                {/* Products */}
                <div>
                  <h4 className="font-bold mb-2 flex items-center gap-2 text-sm"><Package className="h-4 w-4 text-brand_blue" /> المنتجات</h4>
                  {b.products.length === 0 ? <p className="text-xs text-foreground/40 py-4 text-center">لا توجد مبيعات</p> : (
                    <table className="w-full text-xs"><thead><tr className="text-foreground/55 border-b border-border"><th className="py-1.5 text-right">المنتج</th><th className="py-1.5">الكمية</th><th className="py-1.5">معاملات</th><th className="py-1.5 text-left">الإجمالي</th></tr></thead>
                      <tbody className="divide-y divide-border">
                        {b.products.map((p, i) => <tr key={i}><td className="py-1.5 font-bold">{p.name}</td><td className="py-1.5 text-center font-numbers">{p.quantity}</td><td className="py-1.5 text-center font-numbers">{p.count}</td><td className="py-1.5 text-left font-numbers font-bold text-emerald-700">{fmt(p.total)}</td></tr>)}
                      </tbody></table>
                  )}
                </div>
                {/* Countries */}
                <div>
                  <h4 className="font-bold mb-2 flex items-center gap-2 text-sm"><Globe className="h-4 w-4 text-amber-600" /> الدول</h4>
                  {b.countries.length === 0 ? <p className="text-xs text-foreground/40 py-4 text-center">لا توجد مبيعات</p> : (
                    <table className="w-full text-xs"><thead><tr className="text-foreground/55 border-b border-border"><th className="py-1.5 text-right">الدولة</th><th className="py-1.5">معاملات</th><th className="py-1.5 text-left">الإجمالي</th></tr></thead>
                      <tbody className="divide-y divide-border">
                        {b.countries.map((c, i) => <tr key={i}><td className="py-1.5 font-bold">{c.country}</td><td className="py-1.5 text-center font-numbers">{c.count}</td><td className="py-1.5 text-left font-numbers font-bold text-amber-700">{fmt(c.total)}</td></tr>)}
                      </tbody></table>
                  )}
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

/* ─────────── Tab 4: Growth ─────────── */
function GrowthTab({ growth, dim, setDim }) {
  if (!growth) return <Card className="p-12 text-center text-foreground/50">جاري التحميل...</Card>;
  const items = growth.items || [];
  const chartData = items.slice(0, 10).map(i => ({ name: i.name.length > 14 ? i.name.slice(0, 13) + '…' : i.name, السابق: i.previous_total, الحالي: i.current_total }));

  return (
    <div className="space-y-4" data-testid="growth-tab">
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="font-bold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-brand_blue" /> النمو والانخفاض على مستوى الشبكة</h3>
          <div className="flex gap-1 bg-accent rounded-lg p-1">
            <button onClick={() => setDim('product')} data-testid="growth-dim-product" className={`px-3 py-1 rounded-md text-xs font-bold transition ${dim === 'product' ? 'bg-white shadow' : 'text-foreground/60'}`}>حسب المنتج</button>
            <button onClick={() => setDim('country')} data-testid="growth-dim-country" className={`px-3 py-1 rounded-md text-xs font-bold transition ${dim === 'country' ? 'bg-white shadow' : 'text-foreground/60'}`}>حسب الدولة</button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Card className="p-3"><p className="text-xs text-foreground/55">الحالية</p><p className="text-lg font-black font-numbers">{fmt(growth.current.total)} <SAR /></p></Card>
          <Card className="p-3"><p className="text-xs text-foreground/55">السابقة</p><p className="text-lg font-black font-numbers text-foreground/70">{fmt(growth.previous.total)} <SAR /></p></Card>
          <Card className={`p-3 ${growth.growth_amount >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}><p className="text-xs text-foreground/55">فرق النمو</p><p className={`text-lg font-black font-numbers ${growth.growth_amount >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{growth.growth_amount >= 0 ? '+' : ''}{fmt(growth.growth_amount)} <SAR /></p></Card>
          <Card className="p-3"><p className="text-xs text-foreground/55">نسبة النمو</p><p className={`text-lg font-black font-numbers ${growth.growth_percentage >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{growth.growth_percentage >= 0 ? '+' : ''}{growth.growth_percentage}%</p></Card>
        </div>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#374151' }} />
              <YAxis tick={{ fontSize: 11, fill: '#374151' }} />
              <Tooltip formatter={(v) => `${fmt(v)} ر.س`} contentStyle={{ direction: 'rtl' }} />
              <Legend wrapperStyle={{ direction: 'rtl' }} />
              <Bar dataKey="السابق" fill="#94A3B8" radius={[4, 4, 0, 0]} />
              <Bar dataKey="الحالي" fill="#1E40AF" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="growth-table">
            <thead className="bg-accent/40">
              <tr><th className="py-2 px-3 text-right">{dim === 'product' ? 'المنتج' : 'الدولة'}</th><th className="py-2 px-3">الفروع الحالية</th><th className="py-2 px-3 text-left">السابق</th><th className="py-2 px-3 text-left">الحالي</th><th className="py-2 px-3 text-left">الفرق</th><th className="py-2 px-3">النسبة</th><th className="py-2 px-3">الاتجاه</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.length === 0 ? <tr><td colSpan="7" className="py-10 text-center text-foreground/40">لا توجد بيانات</td></tr> :
                items.map((it, i) => (
                  <tr key={i} className="hover:bg-accent/30">
                    <td className="py-2 px-3 font-bold">{it.name}</td>
                    <td className="py-2 px-3 text-center font-numbers">{it.current_branches}</td>
                    <td className="py-2 px-3 text-left font-numbers text-foreground/70">{fmt(it.previous_total)}</td>
                    <td className="py-2 px-3 text-left font-numbers font-bold text-emerald-700">{fmt(it.current_total)}</td>
                    <td className={`py-2 px-3 text-left font-numbers font-bold ${it.growth_amount >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{it.growth_amount >= 0 ? '+' : ''}{fmt(it.growth_amount)}</td>
                    <td className={`py-2 px-3 text-center font-numbers font-bold ${it.growth_percentage >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{it.growth_percentage}%</td>
                    <td className="py-2 px-3 text-center"><TrendBadge trend={it.trend} /></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ─────────── Donut Indicators (top of analytics page) ─────────── */
const DONUT_PALETTE = ['#1E40AF', '#0F766E', '#B45309', '#9333EA', '#DB2777', '#0EA5E9', '#65A30D', '#94A3B8'];

// Build {name,value,count}[] from raw rows — show ALL items (no "أخرى" grouping)
const buildSlices = (rows, nameKey, valueKey, countKey) => {
  return rows
    .map(r => ({
      name: r[nameKey] || '—',
      value: Number(r[valueKey] || 0),
      count: countKey ? Number(r[countKey] || 0) : 0,
    }))
    .filter(r => r.value > 0)
    .sort((a, b) => b.value - a.value);
};

function DonutCard({ title, Icon, slices, accent, testId, valueLabel = 'ر.س', countLabel,
                    onSliceClick, activeName }) {
  const total = slices.reduce((s, r) => s + r.value, 0);
  const isEmpty = total <= 0 || slices.length === 0;

  const donutTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0];
    const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : 0;
    const cnt = p.payload?.count || 0;
    return (
      <div className="bg-white border border-border rounded-md shadow-md px-3 py-2 text-xs" dir="rtl">
        <div className="font-bold mb-0.5" style={{ color: p.payload.fill }}>{p.name}</div>
        <div className="font-numbers">{fmt(p.value)} {valueLabel} <span className="text-foreground/50">({pct}%)</span></div>
        {cnt > 0 && (
          <div className="font-numbers text-foreground/70 mt-0.5">{fmt(cnt)} عملية</div>
        )}
        {onSliceClick && p.name !== 'أخرى' && (
          <div className="mt-1 text-[10px] text-brand_blue font-bold">انقر للتصفية ↗</div>
        )}
      </div>
    );
  };

  const handleClick = (data) => {
    // Recharts onClick passes the segment payload as `data` (or e.payload.payload)
    const name = data?.name || data?.payload?.name;
    if (!name || name === 'أخرى' || !onSliceClick) return;
    onSliceClick(name);
  };

  return (
    <Card className="p-4 flex flex-col" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-md flex items-center justify-center" style={{ background: `${accent}1A`, color: accent }}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-sm truncate">{title}</h4>
          {countLabel && <p className="text-[10px] text-foreground/50">{countLabel}</p>}
        </div>
      </div>
      <div className="relative" style={{ height: 180 }}>
        {isEmpty ? (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-foreground/40">لا توجد بيانات</div>
        ) : (
          <>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={48}
                  outerRadius={75}
                  paddingAngle={2}
                  stroke="#fff"
                  strokeWidth={2}
                  onClick={handleClick}
                  cursor={onSliceClick ? 'pointer' : 'default'}
                >
                  {slices.map((s, i) => {
                    const isActive = activeName && s.name === activeName;
                    return (
                      <Cell
                        key={i}
                        fill={DONUT_PALETTE[i % DONUT_PALETTE.length]}
                        stroke={isActive ? '#0F172A' : '#fff'}
                        strokeWidth={isActive ? 3 : 2}
                      />
                    );
                  })}
                </Pie>
                <Tooltip content={donutTooltip} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <p className="text-[10px] text-foreground/50">الإجمالي</p>
              <p className="text-base font-black font-numbers" style={{ color: accent }}>{fmt(total)}</p>
              <p className="text-[10px] text-foreground/50 -mt-0.5">{valueLabel}</p>
            </div>
          </>
        )}
      </div>
      {!isEmpty && (
        <div className="mt-3 space-y-1.5 text-xs max-h-[120px] overflow-y-auto">
          {slices.map((s, i) => {
            const pct = ((s.value / total) * 100).toFixed(1);
            const clickable = onSliceClick && s.name !== 'أخرى';
            const isActive = activeName && s.name === activeName;
            return (
              <button
                key={i}
                onClick={clickable ? () => onSliceClick(s.name) : undefined}
                className={`flex items-center gap-2 w-full text-right rounded-md px-1.5 py-0.5 transition ${
                  clickable ? 'hover:bg-accent cursor-pointer' : 'cursor-default'
                } ${isActive ? 'bg-brand_blue/10 ring-1 ring-brand_blue/40' : ''}`}
                data-testid={`${testId}-slice-${i}`}
                disabled={!clickable}
              >
                <span className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ background: DONUT_PALETTE[i % DONUT_PALETTE.length] }} />
                <span className="flex-1 truncate font-bold">
                  {s.name}
                  {s.count > 0 && (
                    <span className="ms-1.5 text-[10px] font-normal text-foreground/55 font-numbers">
                      ({fmt(s.count)} عملية)
                    </span>
                  )}
                </span>
                <span className="text-foreground/55 font-numbers">{pct}%</span>
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function DonutSection({ ranking, breakdown, rankingFull, breakdownFull, drillFilters, onSliceClick, showBranchDonut = true }) {
  if (!ranking || !breakdown) return null;

  // Branch donut: ALWAYS show full network (so user can pick another branch).
  // Product/Country/Employee donuts: when any filter is active, reflect the filtered scope.
  const branchSrc = breakdownFull || breakdown;
  const otherSrc = breakdown; // already scoped to drillFilters from the API
  const empSrc = ranking;

  const branchSlices = buildSlices(branchSrc.branches || [], 'branch_name', 'total', 'transactions');
  const employeeSlices = buildSlices(empSrc.rows || [], 'name', 'achieved', 'transactions');
  const prodMap = {}; const cnMap = {};
  (otherSrc.branches || []).forEach(b => {
    (b.products || []).forEach(p => {
      if (!prodMap[p.name]) prodMap[p.name] = { total: 0, count: 0 };
      prodMap[p.name].total += (p.total || 0);
      prodMap[p.name].count += (p.count || 0);
    });
    (b.countries || []).forEach(c => {
      if (!cnMap[c.country]) cnMap[c.country] = { total: 0, count: 0 };
      cnMap[c.country].total += (c.total || 0);
      cnMap[c.country].count += (c.count || 0);
    });
  });
  const productSlices = buildSlices(
    Object.entries(prodMap).map(([name, v]) => ({ name, total: v.total, count: v.count })),
    'name', 'total', 'count',
  );
  const countrySlices = buildSlices(
    Object.entries(cnMap).map(([name, v]) => ({ name, total: v.total, count: v.count })),
    'name', 'total', 'count',
  );

  // Resolve current active name per dimension (so we can highlight the matching slice)
  const dfs = drillFilters || {};
  const activeBranchName = dfs.branch
    ? (branchSrc.branches || []).find(b => b.branch_id === dfs.branch.value)?.branch_name
    : null;
  const activeCountryName = dfs.country?.value || null;
  const activeProductName = dfs.product?.value || null;

  const gridCols = showBranchDonut ? 'lg:grid-cols-4' : 'lg:grid-cols-3';
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-4`} data-testid="donut-indicators">
      {showBranchDonut && (
        <DonutCard
          title="مبيعات الفروع"
          Icon={Building2}
          slices={branchSlices}
          accent="#1E40AF"
          testId="donut-branches"
          countLabel={`${(breakdown.branches || []).length} فرع — انقر للتصفية`}
          onSliceClick={(name) => onSliceClick && onSliceClick('branch', name)}
          activeName={activeBranchName}
        />
      )}
      <DonutCard
        title="مبيعات المنتجات"
        Icon={Package}
        slices={productSlices}
        accent="#0F766E"
        testId="donut-products"
        countLabel={`${Object.keys(prodMap).length} منتج — انقر للتصفية`}
        onSliceClick={(name) => onSliceClick && onSliceClick('product', name)}
        activeName={activeProductName}
      />
      <DonutCard
        title="مبيعات الدول"
        Icon={Globe}
        slices={countrySlices}
        accent="#B45309"
        testId="donut-countries"
        countLabel={`${Object.keys(cnMap).length} دولة — انقر للتصفية`}
        onSliceClick={(name) => onSliceClick && onSliceClick('country', name)}
        activeName={activeCountryName}
      />
      <DonutCard
        title="مبيعات الموظفين"
        Icon={UsersIcon}
        slices={employeeSlices}
        accent="#9333EA"
        testId="donut-employees"
        countLabel={`${(ranking.rows || []).length} موظف — انقر للتفصيل`}
        onSliceClick={(name) => onSliceClick && onSliceClick('employee', name)}
        activeName={null}
      />
    </div>
  );
}
